"use client";

import { useRouter } from "next/navigation";
import React, { type PropsWithChildren, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { twMerge } from "tailwind-merge";

import { env } from "@/env";
import { getFormMaxlength, toErrorString } from "@/globals";
import { BUTTON_STYLES } from "@/styles/buttons";
import { INPUT_STYLES, TYPO_STYLES } from "@/styles/typography";
import { WRAPPER_STYLES } from "@/styles/wrapper";
import { ErrorView } from "@/views/error.view";

type FormEdupageNewsletterModuleProps = {
    buttonLabel: string;
    entityId: string;
    entityType: string;
    successHref: string | undefined;
};

type FormProps = {
    email?: string;
    entityId?: string;
    entityType?: string;
    firstname?: string;
    lastname?: string;
    privacy?: boolean;
    role?: string;
    salutation?: string;
    schoolName?: string;
    schoolType?: string;
    zip?: string;
};

const testData: Required<FormProps> = {
    entityId: "",
    entityType: "",
    email: "max@mustermann.com",
    firstname: "Max",
    lastname: "Musterman",
    privacy: true,
    salutation: "male",
    zip: "58566",
    schoolName: "THG",
    role: "lehrer",
    schoolType: "grundschule",
};

const defaultData: Required<FormProps> = {
    entityId: "",
    entityType: "",
    email: "",
    firstname: "",
    lastname: "",
    privacy: false,
    salutation: "",
    zip: "",
    role: "",
    schoolName: "",
    schoolType: "",
};

export const FormEdupageNewsletterModuleView = ({
    successHref,
    entityId,
    entityType,
    buttonLabel,
    children,
}: PropsWithChildren<FormEdupageNewsletterModuleProps>) => {
    const [error, setError] = useState<string | undefined>();

    const [form, setForm] = useState<FormProps>({
        ...defaultData,
        ...(env.NEXT_PUBLIC_USE_TESTDATA ? testData : {}),
        entityId,
        entityType,
    });

    const { executeRecaptcha } = useGoogleReCaptcha();

    const [processing, setProcessing] = useState(false);

    const router = useRouter();

    const handleChange = (values: FormProps) => {
        setError(undefined);
        setForm((f) => ({
            ...f,
            ...values,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!processing) {
            setProcessing(true);

            try {
                if (executeRecaptcha) {
                    const recaptchaToken = await executeRecaptcha(entityId);

                    const response = await fetch("/api/submit", {
                        method: "POST",
                        body: JSON.stringify({ ...form, recaptchaToken }),
                    });

                    if (response.ok && successHref) {
                        router.push(successHref);
                    } else {
                        throw new Error("Das schlug leider fehl.");
                    }
                } else {
                    throw new Error("Sendung aktuell nicht möglich");
                }
            } catch (error) {
                setError(toErrorString(error));
                setProcessing(false);
            }
        }
    };

    if (successHref) {
        return (
            <div className={twMerge("w-full grid")}>
                <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
                    <div className="bg-white px-8 py-10 rounded-3xl grid sm:grid-cols-6 gap-8">
                        <label className="flex flex-col gap-2 col-span-6 sm:col-span-3">
                            <span
                                className={twMerge(TYPO_STYLES.bodyExtraBold18(), "text-brandDark")}
                            >
                                Name der Schule
                            </span>
                            <input
                                required
                                type="text"
                                placeholder="Name der Schule"
                                className={INPUT_STYLES.input()}
                                defaultValue={form.schoolName}
                                onChange={(e) =>
                                    handleChange({ schoolName: e.target.value || undefined })
                                }
                                pattern=".*\S.*"
                                title="Bitte geben Sie einen gülten Wert ein."
                            />
                        </label>
                        <label className="flex flex-col gap-2 col-span-6 sm:col-span-3 relative">
                            <span
                                className={twMerge(TYPO_STYLES.bodyExtraBold18(), "text-brandDark")}
                            >
                                PLZ
                            </span>
                            <input
                                required
                                type="text"
                                placeholder="PLZ"
                                className={INPUT_STYLES.input()}
                                defaultValue={form.zip}
                                onChange={(e) => handleChange({ zip: e.target.value || undefined })}
                                maxLength={getFormMaxlength("zip")}
                                pattern=".*\S.*"
                                title="Bitte geben Sie einen gülten Wert ein."
                            />
                        </label>
                    </div>
                    <div className="bg-white px-8 py-10 rounded-3xl grid sm:grid-cols-6 gap-8">
                        <span
                            className={twMerge(
                                TYPO_STYLES.bodyExtraBold18(),
                                "col-span-6 text-brandDark",
                            )}
                        >
                            Kontaktdaten
                        </span>
                        <div className="custom-chevron grid col-span-6 sm:col-span-3 sm:col-start-1 sm:col-end-3">
                            <label htmlFor="salutation" className="gap-2 flex flex-col">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "text-brandDark",
                                    )}
                                >
                                    Anrede
                                </span>
                                <select
                                    className={twMerge(INPUT_STYLES.dropdown())}
                                    required
                                    onChange={(e) =>
                                        handleChange({ salutation: e.target.value || undefined })
                                    }
                                    defaultValue={form.salutation}
                                >
                                    <option value="">Auswählen</option>
                                    <option value="male">Herr</option>
                                    <option value="female">Frau</option>
                                    <option value="divers">Divers</option>
                                </select>
                            </label>
                        </div>
                        <label className="flex flex-col gap-2 col-span-6 sm:col-start-1 col-end-4">
                            <span
                                className={twMerge(TYPO_STYLES.bodyExtraBold18(), "text-brandDark")}
                            >
                                Vorname
                            </span>
                            <input
                                required
                                type="text"
                                placeholder="Vorname"
                                className={INPUT_STYLES.input()}
                                defaultValue={form.firstname}
                                onChange={(e) =>
                                    handleChange({ firstname: e.target.value || undefined })
                                }
                                maxLength={getFormMaxlength("firstname")}
                                pattern=".*\S.*"
                                title="Bitte geben Sie einen gülten Wert ein."
                            />
                        </label>
                        <label className="flex flex-col gap-2 col-span-6 sm:col-span-3">
                            <span
                                className={twMerge(TYPO_STYLES.bodyExtraBold18(), "text-brandDark")}
                            >
                                Nachname
                            </span>
                            <input
                                required
                                type="text"
                                placeholder="Nachname"
                                className={INPUT_STYLES.input()}
                                defaultValue={form.lastname}
                                onChange={(e) =>
                                    handleChange({ lastname: e.target.value || undefined })
                                }
                                maxLength={getFormMaxlength("lastname")}
                                pattern=".*\S.*"
                                title="Bitte geben Sie einen gülten Wert ein."
                            />
                        </label>
                        <label className="grid col-span-6 gap-2">
                            <span
                                className={twMerge(TYPO_STYLES.bodyExtraBold18(), "text-brandDark")}
                            >
                                E-Mail
                            </span>
                            <input
                                required
                                type="email"
                                placeholder="E-Mail"
                                defaultValue={form.email}
                                className={INPUT_STYLES.input()}
                                onChange={(e) =>
                                    handleChange({ email: e.target.value || undefined })
                                }
                                maxLength={getFormMaxlength("email")}
                            />
                        </label>
                    </div>
                    <div className="bg-white px-8 py-10 rounded-3xl grid sm:grid-cols-6 gap-8">
                        <span
                            className={twMerge(
                                TYPO_STYLES.bodyExtraBold18(),
                                "col-span-6 text-brandDark",
                            )}
                        >
                            Mit den folgenden zusätzlichen Angaben, können wir Ihnen zielgerichtete
                            Angaben liefern.
                        </span>
                        <div className="custom-chevron grid col-span-6 sm:col-span-3">
                            <label htmlFor="roles" className="gap-2 flex flex-col">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "text-brandDark",
                                    )}
                                >
                                    Ich bin
                                </span>
                                <select
                                    className={twMerge(INPUT_STYLES.dropdown())}
                                    required
                                    onChange={(e) =>
                                        handleChange({ role: e.target.value || undefined })
                                    }
                                    defaultValue={form.role}
                                >
                                    <option value="">Auswählen</option>

                                    <option value="eduPage-administrator">
                                        EduPage Administrator*in
                                    </option>
                                    <option value="schulleitung">Schulleiter*in</option>
                                    <option value="lehrer">Lehrer*in</option>
                                    <option value="stundenplaner">Stundenplaner*in</option>
                                    <option value="anderes">Anderes</option>
                                </select>
                            </label>
                        </div>
                        <div className="custom-chevron grid col-span-6 sm:col-span-3">
                            <label htmlFor="schoolType" className="gap-2 flex flex-col">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "text-brandDark",
                                    )}
                                >
                                    Schulart auswählen
                                </span>
                                <select
                                    className={twMerge(INPUT_STYLES.dropdown())}
                                    required
                                    onChange={(e) =>
                                        handleChange({ schoolType: e.target.value || undefined })
                                    }
                                    defaultValue={form.schoolType}
                                >
                                    <option value="">Auswählen</option>
                                    <option value="mittelschule">Mittelschule</option>
                                    <option value="grundschule">Grundschule</option>
                                    <option value="hauptschule">Hauptschule</option>
                                    <option value="(werk)realschule">Realschule</option>
                                    <option value="gymnasium">Gymnasium</option>
                                    <option value="görderschule">Förderschule</option>
                                    <option value="gesamtschule">Gesamtschule</option>
                                    <option value="gemeinschaftsschule">Gemeinschaftsschule</option>
                                    <option value="berufliche-schule">Berufliche Schule</option>
                                    <option value="anderes">Andere Schulart</option>
                                </select>
                            </label>
                        </div>
                    </div>
                    {children && (
                        <div className="checkbox-formular relative">
                            <label>
                                <input
                                    defaultChecked={form.privacy}
                                    required
                                    type="checkbox"
                                    onChange={(e) => handleChange({ privacy: e.target.checked })}
                                />
                                {children}
                                <span></span>
                            </label>
                        </div>
                    )}
                    <div className={twMerge(WRAPPER_STYLES.HCenterRight(), "gap-6")}>
                        {error && (
                            <p className={twMerge(TYPO_STYLES.bodyNormal18(), "text-redError")}>
                                {error}
                            </p>
                        )}
                        <input
                            type="submit"
                            className={twMerge(BUTTON_STYLES.primary(), "cursor-pointer")}
                            value={processing ? "...wird gesendet" : buttonLabel}
                        />
                    </div>
                </form>
            </div>
        );
    } else {
        return <ErrorView>Success-Page is not provided.</ErrorView>;
    }
};
